import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  ArrowLeft, 
  Send, 
  Clock,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  User,
  Building2,
  Loader2,
  FolderKanban,
  Calendar,
  Upload,
  Link2,
  FileText,
  BookOpen,
  X,
  ExternalLink,
  Paperclip,
  UserPlus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAgency, isProjectManager } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const messagesEndRef = useRef(null);
  
  // New reply enhancements
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [replyLink, setReplyLink] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showKBSearch, setShowKBSearch] = useState(false);
  const [kbArticles, setKbArticles] = useState([]);
  const [selectedKBArticle, setSelectedKBArticle] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  
  // Assignment states
  const [teamMembers, setTeamMembers] = useState([]);
  const [assigningTicket, setAssigningTicket] = useState(false);

  useEffect(() => {
    fetchTicket();
    if (isAgency || isProjectManager) {
      fetchKBArticles();
      fetchTeamMembers();
    }
  }, [ticketId, isAgency, isProjectManager]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicket = async () => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicket(response.data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Erreur lors du chargement du ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchKBArticles = async () => {
    try {
      const res = await api.get('/knowledge/articles');
      setKbArticles(res.data || []);
    } catch (error) {
      console.log('KB articles not available');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await api.get('/users/', { params: { role: 'team_member' } });
      setTeamMembers(res.data || []);
    } catch (error) {
      console.log('Team members not available');
    }
  };

  const handleAssignTicket = async (memberId) => {
    setAssigningTicket(true);
    try {
      await api.put(`/tickets/${ticketId}/assign`, { assigned_to: memberId || null });
      toast.success(memberId ? 'Ticket assigné !' : 'Assignation retirée');
      fetchTicket();
    } catch (error) {
      console.error('Error assigning ticket:', error);
      toast.error('Erreur lors de l\'assignation');
    } finally {
      setAssigningTicket(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }
    
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/uploads/ticket-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setReplyAttachment({
        name: file.name,
        url: res.data.url,
        type: file.type,
        size: file.size
      });
      toast.success('Fichier uploadé');
    } catch (error) {
      console.error('Upload error:', error);
      // Fallback: store file locally for now
      setReplyAttachment({
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
        size: file.size,
        localFile: file
      });
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setSending(true);
    try {
      // Build reply with attachments
      const replyData = { 
        message: replyMessage,
        attachment_url: replyAttachment?.url || null,
        attachment_name: replyAttachment?.name || null,
        link_url: replyLink || null,
        kb_article_id: selectedKBArticle?.id || null,
        kb_article_title: selectedKBArticle?.title || null
      };
      
      await axios.post(
        `${API}/tickets/${ticketId}/reply`,
        replyData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Reset form
      setReplyMessage('');
      setReplyAttachment(null);
      setReplyLink('');
      setSelectedKBArticle(null);
      setShowLinkInput(false);
      fetchTicket();
      toast.success('Réponse envoyée');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Erreur lors de l\'envoi de la réponse');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTicket(prev => ({ ...prev, status: newStatus }));
      toast.success('Statut mis à jour');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700 border-blue-200',
      in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      resolved: 'bg-green-100 text-green-700 border-green-200',
      closed: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    const labels = {
      open: 'Ouvert',
      in_progress: 'En cours',
      resolved: 'Résolu',
      closed: 'Fermé',
    };
    return (
      <Badge variant="outline" className={cn('font-medium', styles[status] || styles.open)}>
        {status === 'resolved' && <CheckCircle2 className="w-3 h-3 mr-1" />}
        {status === 'open' && <AlertCircle className="w-3 h-3 mr-1" />}
        {labels[status] || status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-600',
      normal: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600',
    };
    const labels = {
      low: 'Basse',
      normal: 'Normale',
      high: 'Haute',
      urgent: 'Urgente',
    };
    return (
      <Badge className={cn('font-medium', styles[priority] || styles.normal)}>
        {labels[priority] || priority}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-8 pb-6 pt-32 md:pt-32",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/tickets')}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux tickets
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Messages */}
          <div className="lg:col-span-2 space-y-4">
            {/* Ticket Header */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl" style={{ color: theme.colors?.primary }}>
                      {ticket.subject}
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Ticket #{ticket.id.substring(0, 8)}
                    </p>
                    {/* Metadata: created_by, updated_at */}
                    {(ticket.created_by_name || ticket.updated_at) && (
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                        {ticket.created_by_name && (
                          <span>
                            Créé par {ticket.created_by_name}
                            {ticket.created_at && ` le ${new Date(ticket.created_at).toLocaleDateString('fr-FR')}`}
                          </span>
                        )}
                        {ticket.updated_at && ticket.updated_by_name && (
                          <span>
                            • Modifié par {ticket.updated_by_name} le {new Date(ticket.updated_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(ticket.status)}
                    {getPriorityBadge(ticket.priority)}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Messages */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {/* Initial Message */}
                  <div className="flex gap-3">
                    <Avatar 
                      name={ticket.user_name || 'Client'}
                      size="md"
                      type="client"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{ticket.user_name || 'Client'}</span>
                        <Badge variant="outline" className="text-xs">Client</Badge>
                        <span className="text-xs text-slate-400">
                          {formatDate(ticket.created_at)}
                        </span>
                      </div>
                      <div className="bg-slate-100 rounded-lg p-3 space-y-2">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {ticket.messages?.[0]?.message || ticket.message || 'Aucun message initial'}
                        </p>
                        
                        {/* Initial message attachment */}
                        {(ticket.attachment_url || ticket.messages?.[0]?.attachment_url) && (
                          <div className="pt-2 border-t border-slate-200/50">
                            <a 
                              href={(() => {
                                const url = ticket.attachment_url || ticket.messages?.[0]?.attachment_url;
                                return url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                              })()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                            >
                              <Paperclip className="w-3 h-3" />
                              Pièce jointe
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                        
                        {/* Initial message link */}
                        {(ticket.link_url || ticket.messages?.[0]?.link_url) && (
                          <div className={!ticket.attachment_url && !ticket.messages?.[0]?.attachment_url ? "pt-2 border-t border-slate-200/50" : ""}>
                            <a 
                              href={ticket.link_url || ticket.messages?.[0]?.link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                            >
                              <Link2 className="w-3 h-3" />
                              {(ticket.link_url || ticket.messages?.[0]?.link_url).substring(0, 50)}...
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Replies */}
                  {ticket.messages?.slice(1).map((msg, index) => {
                    const isAgencyReply = msg.user_id !== ticket.user_id;
                    return (
                      <div key={msg.id || index} className={cn("flex gap-3", isAgencyReply && "flex-row-reverse")}>
                        <Avatar 
                          name={isAgencyReply ? 'Agence' : (ticket.user_name || 'Client')}
                          size="md"
                          type={isAgencyReply ? 'agency' : 'client'}
                        />
                        <div className={cn("flex-1", isAgencyReply && "flex flex-col items-end")}>
                          <div className={cn("flex items-center gap-2 mb-1", isAgencyReply && "flex-row-reverse")}>
                            <span className="font-semibold text-sm">
                              {isAgencyReply ? 'Équipe Support' : (ticket.user_name || 'Client')}
                            </span>
                            <Badge variant="outline" className={cn(
                              "text-xs",
                              isAgencyReply ? "bg-primary/10 text-primary border-primary/20" : ""
                            )}>
                              {isAgencyReply ? 'Agence' : 'Client'}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                          <div className={cn(
                            "rounded-lg p-3 max-w-[85%] space-y-2",
                            isAgencyReply 
                              ? "bg-primary/10 text-slate-800" 
                              : "bg-slate-100"
                          )}>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                            
                            {/* Attachments display */}
                            {(msg.attachment_url || msg.link_url || msg.kb_article_title) && (
                              <div className="space-y-1 pt-2 border-t border-slate-200/50">
                                {msg.attachment_url && (
                                  <a 
                                    href={msg.attachment_url.startsWith('http') ? msg.attachment_url : `${process.env.REACT_APP_BACKEND_URL}${msg.attachment_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                                  >
                                    <Paperclip className="w-3 h-3" />
                                    {msg.attachment_name || 'Pièce jointe'}
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                                {msg.link_url && (
                                  <a 
                                    href={msg.link_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                                  >
                                    <Link2 className="w-3 h-3" />
                                    {msg.link_url.substring(0, 50)}...
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                                {msg.kb_article_title && (
                                  <div 
                                    className="flex items-center gap-1.5 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded cursor-pointer hover:bg-purple-100 transition-colors"
                                    onClick={() => {
                                      if (msg.kb_article_id) {
                                        navigate(`/knowledge/${msg.kb_article_id}`);
                                      }
                                    }}
                                    title="Voir l'article"
                                  >
                                    <BookOpen className="w-3 h-3" />
                                    Article KB: {msg.kb_article_title}
                                    <ExternalLink className="w-3 h-3 ml-1" />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply Form */}
                {ticket.status !== 'closed' && (
                  <form onSubmit={handleSendReply} className="mt-4 pt-4 border-t">
                    <div className="flex gap-3">
                      <Avatar 
                        name={isAgency ? 'Agence' : (user?.name || 'Moi')}
                        size="md"
                        type={isAgency ? 'agency' : 'client'}
                      />
                      <div className="flex-1 space-y-3">
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder="Écrivez votre réponse..."
                          className="min-h-[100px] resize-none"
                          disabled={sending}
                        />
                        
                        {/* Attachments preview */}
                        {(replyAttachment || replyLink || selectedKBArticle) && (
                          <div className="flex flex-wrap gap-2">
                            {replyAttachment && (
                              <Badge variant="outline" className="gap-1 py-1.5 px-2.5">
                                <Paperclip className="w-3 h-3" />
                                {replyAttachment.name}
                                <button
                                  type="button"
                                  onClick={() => setReplyAttachment(null)}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            )}
                            {replyLink && (
                              <Badge variant="outline" className="gap-1 py-1.5 px-2.5 bg-blue-50 text-blue-700">
                                <Link2 className="w-3 h-3" />
                                Lien ajouté
                                <button
                                  type="button"
                                  onClick={() => { setReplyLink(''); setShowLinkInput(false); }}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            )}
                            {selectedKBArticle && (
                              <Badge variant="outline" className="gap-1 py-1.5 px-2.5 bg-purple-50 text-purple-700">
                                <BookOpen className="w-3 h-3" />
                                {selectedKBArticle.title}
                                <button
                                  type="button"
                                  onClick={() => setSelectedKBArticle(null)}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Link input */}
                        {showLinkInput && (
                          <div className="flex gap-2">
                            <Input
                              type="url"
                              value={replyLink}
                              onChange={(e) => setReplyLink(e.target.value)}
                              placeholder="https://example.com/page"
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowLinkInput(false)}
                            >
                              OK
                            </Button>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          {/* Action buttons */}
                          <div className="flex gap-1">
                            {/* Hidden file input */}
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              className="hidden"
                              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip"
                            />
                            
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingAttachment}
                              title="Joindre un fichier"
                            >
                              {uploadingAttachment ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </Button>
                            
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowLinkInput(!showLinkInput)}
                              title="Ajouter un lien"
                            >
                              <Link2 className="w-4 h-4" />
                            </Button>
                            
                            {isAgency && kbArticles.length > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowKBSearch(true)}
                                title="Insérer un article KB"
                              >
                                <BookOpen className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          
                          <Button 
                            type="submit" 
                            disabled={!replyMessage.trim() || sending}
                            style={{ backgroundColor: theme.colors?.accent }}
                          >
                            {sending ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Envoyer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                {ticket.status === 'closed' && (
                  <div className="mt-4 pt-4 border-t text-center text-slate-500 text-sm">
                    <CheckCircle2 className="w-5 h-5 mx-auto mb-2 text-green-500" />
                    Ce ticket est fermé. Vous ne pouvez plus y répondre.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Ticket Info */}
          <div className="space-y-4">
            {/* Status Actions */}
            {(isAgency || isProjectManager) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Changer le statut</label>
                    <Select 
                      value={ticket.status} 
                      onValueChange={handleStatusChange}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Ouvert</SelectItem>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="resolved">Résolu</SelectItem>
                        <SelectItem value="closed">Fermé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Assign ticket */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Assigner à
                    </label>
                    <Select 
                      value={ticket.assigned_to || '_none'} 
                      onValueChange={(v) => handleAssignTicket(v === '_none' ? null : v)}
                      disabled={assigningTicket}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Non assigné" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Non assigné</SelectItem>
                        {teamMembers.map(member => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.first_name} {member.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {ticket.assigned_to_name && (
                      <p className="text-xs text-slate-500">
                        Actuellement assigné à: <span className="font-medium">{ticket.assigned_to_name}</span>
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ticket Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Créé par</p>
                    <p className="text-sm font-medium">{ticket.user_name || 'Client'}</p>
                  </div>
                </div>

                {ticket.assigned_to_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <UserPlus className="w-4 h-4 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Assigné à</p>
                      <p className="text-sm font-medium text-blue-600">{ticket.assigned_to_name}</p>
                    </div>
                  </div>
                )}

                {ticket.project_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <FolderKanban className="w-4 h-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Projet</p>
                      <p className="text-sm font-medium">{ticket.project_name}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Créé le</p>
                    <p className="text-sm font-medium">{formatDate(ticket.created_at)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Messages</p>
                    <p className="text-sm font-medium">{ticket.messages?.length || 1} message(s)</p>
                  </div>
                </div>

                {ticket.updated_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Dernière mise à jour</p>
                      <p className="text-sm font-medium">{formatDate(ticket.updated_at)}</p>
                    </div>
                  </div>
                )}

                {/* Resolution Time - Agency only */}
                {(isAgency || isProjectManager) && ticket.status === 'resolved' && ticket.resolved_at && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-green-600 font-medium">Délai de traitement</p>
                      <p className="text-sm font-bold text-green-700">
                        {(() => {
                          const created = new Date(ticket.created_at);
                          const resolved = new Date(ticket.resolved_at);
                          const diffMs = resolved - created;
                          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                          const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                          
                          if (diffHours >= 24) {
                            const days = Math.floor(diffHours / 24);
                            const remainingHours = diffHours % 24;
                            return `${days}j ${remainingHours}h`;
                          }
                          return `${diffHours}h ${diffMinutes}min`;
                        })()}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      {/* KB Article Selection Modal */}
      <Dialog open={showKBSearch} onOpenChange={setShowKBSearch}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Base de connaissances
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {kbArticles.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Aucun article disponible</p>
            ) : (
              kbArticles.map((article) => (
                <div
                  key={article.id}
                  onClick={() => {
                    setSelectedKBArticle(article);
                    setShowKBSearch(false);
                  }}
                  className={cn(
                    "p-3 rounded-lg border cursor-pointer transition-all hover:bg-slate-50",
                    selectedKBArticle?.id === article.id && "border-2 bg-slate-50"
                  )}
                  style={{ 
                    borderColor: selectedKBArticle?.id === article.id ? theme.colors?.accent : undefined 
                  }}
                >
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                        {article.title}
                      </p>
                      {article.summary && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {article.summary}
                        </p>
                      )}
                      {article.category && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          {article.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKBSearch(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetailPage;
